if (!customElements.get('delivery-date-picker-v2')) {
  class DeliveryDatePickerV2 extends HTMLElement {
    constructor() {
      super();

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleEscape = this.handleEscape.bind(this);
      this.handlePostcodeInput = this.handlePostcodeInput.bind(this);
      this.handlePostcodeBlur = this.handlePostcodeBlur.bind(this);
      this.handlePostcodeSubmit = this.handlePostcodeSubmit.bind(this);
      this.handleConfirm = this.handleConfirm.bind(this);
      this.handleAcknowledge = this.handleAcknowledge.bind(this);
      this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handlePanelInteraction = this.handlePanelInteraction.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);

      this.pendingAttributeUpdate = Promise.resolve();
      this.updateQueue = Promise.resolve();
      this.isConnectedOnce = false;
    }

    connectedCallback() {
      if (this.isConnectedOnce) {
        this.cacheElements();
        this.hydrateFromData();
        this.bindEvents();
        return;
      }

      this.isConnectedOnce = true;
      this.cacheElements();
      this.hydrateFromData();

      if (this.data.disable) {
        this.hidden = true;
        return;
      }

      this.initialiseState();
      this.bindEvents();
      this.syncFromSavedAttributes();
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    cacheElements() {
      this.trigger = this.querySelector('[data-trigger]');
      this.panel = this.querySelector('[data-panel]');
      this.panelInner = this.querySelector('.delivery-date-picker-v2__panel-inner');
      this.summaryField = this.querySelector('[data-summary-field]');
      this.summaryText = this.querySelector('[data-summary-text]');
      this.summaryIcon = this.querySelector('[data-summary-icon]');
      this.postcodeInput = this.querySelector('[data-postcode-input]');
      this.postcodeButton = this.querySelector('[data-postcode-button]');
      this.postcodeError = this.querySelector('[data-postcode-error]');
      this.successMessage = this.querySelector('[data-success-message]');
      this.excludedMessage = this.querySelector('[data-excluded-message]');
      this.calendar = this.querySelector('[data-calendar]');
      this.calendarTitle = this.querySelector('[data-calendar-title]');
      this.calendarGrid = this.querySelector('[data-calendar-grid]');
      this.confirmButton = this.querySelector('[data-confirm-button]');
      this.acknowledgeButton = this.querySelector('[data-acknowledge-button]');
      this.selectedDateInput = this.querySelector('[data-selected-delivery-date]');
      this.stepPostcode = this.querySelector('[data-step="postcode"]');
      this.stepDate = this.querySelector('[data-step="date"]');
      this.stepToggles = this.querySelectorAll('[data-step-toggle]');
      this.navButtons = this.querySelectorAll('[data-calendar-nav]');
      this.checkoutButton = document.querySelector('#CartDrawerV2-Checkout');
      this.htmlElement = document.documentElement;
    }

    hydrateFromData() {
      const rawData = this.querySelector('[data-delivery-date-picker-data]')?.textContent;
      this.data = rawData ? JSON.parse(rawData) : { disable: true };
      this.blackoutDates = new Set(this.data.blackout || []);
      this.postcodeExclusions = this.data.postcodeExclusions || [];
      this.extraDayPostcodes = this.data.extraDayPostcodes || [];
    }

    initialiseState() {
      this.isOpen = false;
      this.postcodeStatus = 'initial';
      this.selectedDate = this.parseDate(this.data.selectedDeliveryDate);
      this.minDate = null;
      this.maxDate = this.parseDate(this.data.maxDate);
      this.viewDate = new Date();

      this.computeMinDate();

      if (this.selectedDate && this.selectedDate < this.minDate) {
        this.selectedDate = null;
        this.data.selectedDeliveryDate = '';
        this.persistAttributes({
          'Delivery Date': '',
        });
      }

      this.viewDate = new Date((this.selectedDate || this.minDate).getFullYear(), (this.selectedDate || this.minDate).getMonth(), 1);
      this.updateSummary(this.data.selectedDeliveryDate || '');
      this.renderCalendar();
      this.updateStepState('postcode', true);
      this.updateStepState('date', false);
      this.updateStatusUI();
      this.persistAttributes(
        { minDate: this.formatDate(this.minDate) },
        { broadcast: false, showLoading: false }
      );
    }

    syncFromSavedAttributes() {
      const savedPostcode = (this.data.selectedPostcode || '').replace(/\s+/g, '').toUpperCase();

      if (!savedPostcode) {
        return;
      }

      this.postcodeInput.value = this.formatPostcodeDisplay(savedPostcode);
      const validation = this.validatePostcode(savedPostcode);

      if (!validation.isValid && !validation.isExcluded) {
        return;
      }

      this.applyPostcodeState(validation, { advance: false });
    }

    bindEvents() {
      if (this.bound) {
        return;
      }

      this.bound = true;
      this.trigger?.addEventListener('click', () => this.togglePanel());
      this.panel?.addEventListener('click', this.handlePanelInteraction);
      this.panel?.addEventListener('keydown', this.handlePanelInteraction);
      this.postcodeInput?.addEventListener('input', this.handlePostcodeInput);
      this.postcodeInput?.addEventListener('blur', this.handlePostcodeBlur);
      this.postcodeInput?.addEventListener('keydown', event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.handlePostcodeSubmit();
        }
      });
      this.postcodeButton?.addEventListener('click', this.handlePostcodeSubmit);
      this.confirmButton?.addEventListener('click', this.handleConfirm);
      this.acknowledgeButton?.addEventListener('click', this.handleAcknowledge);
      this.stepToggles.forEach(button => {
        button.addEventListener('click', () => {
          this.toggleStep(button.dataset.stepToggle);
        });
      });
      this.navButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.changeMonth(button.dataset.calendarNav === 'next' ? 1 : -1);
        });
      });

      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleEscape);
      document.addEventListener('liquid-ajax-cart:queue-end', this.handleCartUpdate);
      document.addEventListener('cart:updated', this.handleCartUpdate);
      this.checkoutButton?.addEventListener('click', this.handleCheckoutClick);
      window.addEventListener('resize', this.handleViewportChange);
      window.visualViewport?.addEventListener('resize', this.handleViewportChange);
      window.visualViewport?.addEventListener('scroll', this.handleViewportChange);
    }

    unbindEvents() {
      if (!this.bound) {
        return;
      }

      this.bound = false;
      this.panel?.removeEventListener('click', this.handlePanelInteraction);
      this.panel?.removeEventListener('keydown', this.handlePanelInteraction);
      this.postcodeInput?.removeEventListener('blur', this.handlePostcodeBlur);
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleEscape);
      document.removeEventListener('liquid-ajax-cart:queue-end', this.handleCartUpdate);
      document.removeEventListener('cart:updated', this.handleCartUpdate);
      this.checkoutButton?.removeEventListener('click', this.handleCheckoutClick);
      window.removeEventListener('resize', this.handleViewportChange);
      window.visualViewport?.removeEventListener('resize', this.handleViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.handleViewportChange);
    }

    handleCartUpdate(event) {
      const cart = event?.detail?.cart;

      if (!cart || !cart.attributes) {
        return;
      }

      const deliveryDate = cart.attributes['Delivery Date'] || '';
      const postcode = cart.attributes.Postcode || '';

      if (postcode && postcode !== this.postcodeInput.value.replace(/\s+/g, '').toUpperCase()) {
        this.postcodeInput.value = this.formatPostcodeDisplay(postcode);
        const validation = this.validatePostcode(postcode);
        if (validation.isValid || validation.isExcluded) {
          this.applyPostcodeState(validation, { advance: false });
        }
      }

      if (deliveryDate !== this.summaryText?.textContent?.trim()) {
        this.selectedDate = this.parseDate(deliveryDate);
        this.data.selectedDeliveryDate = deliveryDate;
        this.updateSummary(deliveryDate);
        this.renderCalendar();
      }
    }

    handleDocumentClick(event) {
      const clickPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const clickedInside = clickPath.includes(this) || this.contains(event.target);

      if (!this.isOpen || clickedInside) {
        return;
      }

      this.closePanel();
    }

    handleEscape(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.closePanel();
      }
    }

    handlePanelInteraction(event) {
      event.stopPropagation();
    }

    handlePostcodeInput() {
      this.postcodeInput.value = this.postcodeInput.value.toUpperCase();
      this.postcodeStatus = 'initial';
      this.updateStatusUI();
    }

    handlePostcodeBlur() {
      this.postcodeInput.value = this.formatPostcodeDisplay(this.postcodeInput.value);
    }

    handlePostcodeSubmit() {
      const postcode = this.postcodeInput.value.replace(/\s+/g, '').toUpperCase();
      const validation = this.validatePostcode(postcode);

      this.applyPostcodeState(validation, { advance: true });
    }

    handleConfirm() {
      if (!this.selectedDate) {
        return;
      }

      const formattedDate = this.formatDate(this.selectedDate);
      this.data.selectedDeliveryDate = formattedDate;
      this.updateSummary(formattedDate);
      this.persistAttributes({
        'Delivery Date': formattedDate,
      });
      this.closePanel();
    }

    handleAcknowledge() {
      this.persistAttributes({
        'Delivery Date': '',
      })
      this.closePanel();
    }

    async handleCheckoutClick(event) {
      if (!this.isAttributeUpdatePending()) {
        return;
      }

      event.preventDefault();

      try {
        await this.pendingAttributeUpdate;
        window.location.assign(this.checkoutButton.href);
      } catch (error) {
        console.error('[DeliveryDatePickerV2] Failed to persist delivery attributes', error);
      }
    }

    togglePanel(forceState) {
      const nextState = typeof forceState === 'boolean' ? forceState : !this.isOpen;

      if (nextState) {
        this.openPanel();
        return;
      }

      this.closePanel();
    }

    openPanel() {
      this.isOpen = true;
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.updatePanelViewportConstraints();
    }

    closePanel() {
      this.isOpen = false;
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    handleViewportChange() {
      if (!this.isOpen) {
        return;
      }

      this.updatePanelViewportConstraints();
    }

    updatePanelViewportConstraints() {
      if (!this.panel || !this.panelInner) {
        return;
      }

      window.requestAnimationFrame(() => {
        if (!this.isOpen || this.panel.hidden) {
          return;
        }

        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const panelRect = this.panel.getBoundingClientRect();
        const maxHeight = Math.max(Math.min(panelRect.bottom, viewportHeight) - 16, 0);

        this.panelInner.style.maxHeight = `${maxHeight}px`;
      });
    }

    toggleStep(stepName) {
      if (stepName === 'postcode') {
        const nextOpen = !this.stepPostcode.classList.contains('is-open');
        this.updateStepState('postcode', nextOpen);
        return;
      }

      if (this.postcodeStatus === 'initial' || this.postcodeStatus === 'invalid') {
        return;
      }

      const nextOpen = !this.stepDate.classList.contains('is-open');
      this.updateStepState('date', nextOpen);
    }

    updateStepState(stepName, isOpen) {
      const step = stepName === 'postcode' ? this.stepPostcode : this.stepDate;
      const toggle = step?.querySelector('[data-step-toggle]');
      if (!step || !toggle) {
        return;
      }

      step.classList.toggle('is-open', isOpen);
      toggle.setAttribute('aria-expanded', String(isOpen));
    }

    applyPostcodeState(validation, options = {}) {
      const { advance = true } = options;

      this.postcodeInput.value = this.formatPostcodeDisplay(validation.postcode);

      if (validation.isValid) {
        this.postcodeStatus = validation.requiresExtraDay ? 'valid-extra-day' : 'valid';

        if (validation.requiresExtraDay) {
          const extraMinDate = new Date(this.minDate);
          extraMinDate.setDate(extraMinDate.getDate() + 1);
          this.minDateForSelection = extraMinDate;
        } else {
          this.minDateForSelection = new Date(this.minDate);
        }

        if (this.selectedDate && this.selectedDate < this.minDateForSelection) {
          this.selectedDate = null;
          this.persistAttributes({
            'Delivery Date': '',
          });
          this.updateSummary('');
        }

        if (this.data.preselectMinDate && !this.selectedDate) {
          const preselectedDate = new Date(this.minDateForSelection);
          const formattedPreselectedDate = this.formatDate(preselectedDate);

          this.selectedDate = preselectedDate;
          this.data.selectedDeliveryDate = formattedPreselectedDate;
          this.updateSummary(formattedPreselectedDate);
          this.persistAttributes({
            Postcode: validation.postcode,
            minDate: this.formatDate(this.minDateForSelection),
            'Delivery Date': formattedPreselectedDate,
          });
        } else {
          this.persistAttributes({
            Postcode: validation.postcode,
            minDate: this.formatDate(this.minDateForSelection),
          });
        }

        this.viewDate = new Date(this.minDateForSelection.getFullYear(), this.minDateForSelection.getMonth(), 1);
        this.renderCalendar();
        this.updateStatusUI();

        if (advance) {
          this.updateStepState('postcode', false);
          this.updateStepState('date', true);
        }

        return;
      }

      if (validation.isExcluded) {
        this.postcodeStatus = 'excluded';
        this.minDateForSelection = null;
        this.persistAttributes({
          Postcode: validation.postcode,
          'Delivery Date': '',
          minDate: '',
        });
        this.selectedDate = null;
        this.data.selectedDeliveryDate = '';
        this.updateSummary('');
        this.renderCalendar();
        this.updateStatusUI();

        if (advance) {
          this.updateStepState('postcode', false);
          this.updateStepState('date', true);
        }

        return;
      }

      this.postcodeStatus = 'invalid';
      this.minDateForSelection = null;
      this.persistAttributes({
        Postcode: '',
        'Delivery Date': '',
        minDate: '',
      });
      this.selectedDate = null;
      this.data.selectedDeliveryDate = '';
      this.updateSummary('');
      this.renderCalendar();
      this.updateStatusUI();

      if (advance) {
        this.updateStepState('postcode', true);
        this.updateStepState('date', false);
      }
    }

    updateStatusUI() {
      const isValid = this.postcodeStatus === 'valid' || this.postcodeStatus === 'valid-extra-day';
      const isExcluded = this.postcodeStatus === 'excluded';
      const isInvalid = this.postcodeStatus === 'invalid';

      this.postcodeError?.classList.toggle('is-hidden', !isInvalid);
      this.successMessage?.classList.toggle('is-hidden', !isValid);
      this.excludedMessage?.classList.toggle('is-hidden', !isExcluded);
      this.calendar?.classList.toggle('is-hidden', !isValid);
      this.confirmButton?.classList.toggle('is-hidden', !isValid);
      this.acknowledgeButton?.classList.toggle('is-hidden', !isExcluded);

      if (this.confirmButton) {
        this.confirmButton.disabled = !isValid || !this.selectedDate;
      }
    }

    changeMonth(offset) {
      const nextMonth = new Date(this.viewDate);
      nextMonth.setMonth(nextMonth.getMonth() + offset);

      const firstAllowedMonth = new Date((this.minDateForSelection || this.minDate).getFullYear(), (this.minDateForSelection || this.minDate).getMonth(), 1);
      const lastAllowedMonth = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1);

      if (nextMonth < firstAllowedMonth || nextMonth > lastAllowedMonth) {
        return;
      }

      this.viewDate = nextMonth;
      this.renderCalendar();
    }

    renderCalendar() {
      if (!this.calendarTitle || !this.calendarGrid) {
        return;
      }

      this.calendarGrid.innerHTML = '';
      this.calendarTitle.textContent = this.viewDate.toLocaleString('en-GB', {
        month: 'long',
        year: 'numeric'
      });

      const minSelectableDate = this.minDateForSelection || this.minDate;
      const monthStart = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1);
      const gridStart = new Date(monthStart);
      const offset = (monthStart.getDay() + 6) % 7;
      gridStart.setDate(gridStart.getDate() - offset);

      for (let index = 0; index < 42; index += 1) {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + index);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'delivery-date-picker-v2__calendar-day';
        button.textContent = String(date.getDate());

        const isOutsideMonth = date.getMonth() !== this.viewDate.getMonth();
        const isDisabled = !this.isDateSelectable(date, minSelectableDate);
        const isSelected = this.isSameDate(date, this.selectedDate);
        const isToday = this.formatDate(date) === this.formatDate(new Date());

        if (isToday) {
          button.classList.add('is-today');
        }

        if (isOutsideMonth) {
          button.classList.add('is-outside-month');
          button.textContent = '';
        }

        if (isDisabled) {
          button.disabled = true;
          button.classList.add('is-disabled');
        } else {
          button.addEventListener('click', () => {
            this.selectedDate = date;
            this.confirmButton.disabled = false;
            this.renderCalendar();
          });
        }

        if (isSelected) {
          button.classList.add('is-selected');
          button.setAttribute('aria-pressed', 'true');
        }

        this.calendarGrid.appendChild(button);
      }

      this.updateNavState(minSelectableDate);
    }

    updateNavState(minSelectableDate) {
      const firstAllowedMonth = new Date(minSelectableDate.getFullYear(), minSelectableDate.getMonth(), 1);
      const lastAllowedMonth = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1);
      const currentMonth = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1);

      this.navButtons.forEach(button => {
        const isNext = button.dataset.calendarNav === 'next';
        button.disabled = isNext ? currentMonth >= lastAllowedMonth : currentMonth <= firstAllowedMonth;
      });
    }

    isDateSelectable(date, minSelectableDate) {
      const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const minDate = new Date(minSelectableDate.getFullYear(), minSelectableDate.getMonth(), minSelectableDate.getDate());
      const maxDate = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate());
      const isWeekend = compareDate.getDay() === 0 || compareDate.getDay() === 6;
      const isBlackout = this.blackoutDates.has(this.formatDate(compareDate));

      return !isWeekend && !isBlackout && compareDate >= minDate && compareDate <= maxDate;
    }

    validatePostcode(postcodeValue) {
      const postcode = postcodeValue.replace(/\s+/g, '').toUpperCase();
      const substringNum = postcode.length - 3;
      const postcodePrefix = postcode.substring(0, substringNum);
      const postcodeRegex = /^[A-Z]{1,2}[0-9RCHNQ][0-9A-Z]?\s?[0-9][ABD-HJLNP-UW-Z]{2}$|^[A-Z]{2}-?[0-9]{4}$/;

      let isExcluded = false;

      for (let index = 0; index < this.postcodeExclusions.length; index += 1) {
        const excluded = this.postcodeExclusions[index].split('*')[0];
        if (!excluded) {
          continue;
        }

        if (excluded.length < 3) {
          if (postcodePrefix.substring(0, excluded.length) === excluded) {
            isExcluded = true;
            break;
          }
        } else if (postcodePrefix === excluded) {
          isExcluded = true;
          break;
        }
      }

      const isValid = postcodeRegex.test(postcode) && !isExcluded;
      const requiresExtraDay = this.extraDayPostcodes.includes(postcodePrefix);

      return {
        postcode,
        postcodePrefix,
        isValid,
        isExcluded,
        requiresExtraDay
      };
    }

    computeMinDate() {
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      currentDate.setDate(currentDate.getDate() + Number(this.data.daysToOffset || 0));

      if (currentDate.getDay() === 6) {
        currentDate.setDate(currentDate.getDate() + 1);
      }

      let minDate = new Date(currentDate);
      let leadDays = Math.max(Number(this.data.daysToAdd || 0), Number(this.data.globalLeadTime || 0));

      if (leadDays > 0) {
        leadDays += 1;
      }

      if (minDate.getDay() === 0 || minDate.getDay() === 6) {
        leadDays = Math.max(leadDays - 1, 0);
      }

      while (leadDays > 0) {
        minDate.setDate(minDate.getDate() + 1);

        if (this.isBusinessDay(minDate)) {
          leadDays -= 1;
        }
      }

      const currentTimeInLondon = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'Europe/London' })
      );

      if (currentTimeInLondon.getHours() >= Number(this.data.cutOffTime || 0)) {
        minDate.setDate(minDate.getDate() + 1);
      }

      while (!this.isBusinessDay(minDate)) {
        minDate.setDate(minDate.getDate() + 1);
      }

      this.minDate = minDate;
      this.minDateForSelection = new Date(minDate);
    }

    isBusinessDay(date) {
      const day = date.getDay();
      return day !== 0 && day !== 6 && !this.blackoutDates.has(this.formatDate(date));
    }

    updateSummary(value) {
      const displayValue = value || this.data.placeholder;
      this.summaryText.textContent = displayValue;
      this.summaryText.classList.toggle('has-value', Boolean(value));
      this.summaryField?.classList.toggle('has-value', Boolean(value));
      this.summaryField?.classList.toggle('is-empty', !value);
      this.selectedDateInput.value = value;
    }

    isAttributeUpdatePending() {
      return this.updatePending;
    }

    persistAttributes(attributes, options = {}) {
      const { broadcast = true, showLoading = true } = options;

      this.updatePending = true;
      if (showLoading) {
        this.checkoutButton?.classList.add('loading');
        this.htmlElement.classList.add('js-ajax-cart-processing');
      }

      const queuedUpdate = this.updateQueue.then(async () => {
        const cart = await this.getCart();
        const mergedAttributes = {
          ...(cart?.attributes || {}),
          ...attributes
        };

        const changed = Object.keys(attributes).some(key => {
          return (cart?.attributes?.[key] || '') !== (mergedAttributes[key] || '');
        });

        if (!changed) {
          return cart;
        }

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'x-radiant-cart-update-source': 'delivery-date-picker'
          },
          body: JSON.stringify({
            attributes: mergedAttributes
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(`HTTP error! status: ${response.status}`);
          error.response = { data: errorData };
          throw error;
        }

        const updatedCart = await this.getCart();
        if (broadcast) {
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: updatedCart }
          }));
        }

        return updatedCart;
      }).catch(error => {
        console.error('[DeliveryDatePickerV2] ERROR', error);
        if (error?.response?.data?.description) {
          alert(error.response.data.description);
        }
        throw error;
      }).finally(() => {
        this.updatePending = false;
        if (showLoading) {
          this.checkoutButton?.classList.remove('loading');
          this.htmlElement.classList.remove('js-ajax-cart-processing');
        }
      });

      this.updateQueue = queuedUpdate.catch(() => {});
      this.pendingAttributeUpdate = queuedUpdate.catch(() => {});

      return queuedUpdate;
    }

    async getCart() {
      const response = await fetch('/cart.js', {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    }

    parseDate(value) {
      if (!value) {
        return null;
      }

      const parts = value.split('/');
      if (parts.length !== 3) {
        return null;
      }

      const date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
      date.setHours(0, 0, 0, 0);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    formatDate(date) {
      if (!date) {
        return '';
      }

      return date.toLocaleDateString('en-GB');
    }

    formatPostcodeDisplay(postcodeValue) {
      const postcode = String(postcodeValue || '').replace(/\s+/g, '').toUpperCase();

      if (postcode.length <= 3) {
        return postcode;
      }

      return `${postcode.slice(0, -3)} ${postcode.slice(-3)}`;
    }

    isSameDate(leftDate, rightDate) {
      if (!leftDate || !rightDate) {
        return false;
      }

      return (
        leftDate.getFullYear() === rightDate.getFullYear()
        && leftDate.getMonth() === rightDate.getMonth()
        && leftDate.getDate() === rightDate.getDate()
      );
    }
  }

  customElements.define('delivery-date-picker-v2', DeliveryDatePickerV2);
}
